/* eslint-disable @typescript-eslint/camelcase */
import { ISandbox } from 'holberton-school-intranet-api';
import * as React from 'react';
import { ReactElement, useEffect, useState } from 'react';
import { isSafari } from 'react-device-detect';

import { get, post } from '../../api/utils';
import CopyOnClick from '../common/CopyOnClick';

function getSandboxHost(web: string): string | null {
    if (!web) {
        return null;
    }

    try {
        const url = web.includes('://')
            ? web.replace('PORT', '0')
            : `https://${web.replace('PORT', '0')}`;

        return new URL(url).hostname;
    } catch {
        return null;
    }
}

export interface ISandboxProps extends ISandbox {
    csrfToken: string;
    sandboxesUri: string;
    deleteSandbox: Function;
}

export default function Sandbox(props: ISandboxProps): ReactElement {
    const { csrfToken, sandboxesUri, deleteSandbox, ...sandbox } = props;
    const [sandboxLocalState, setSandboxLocalState] = useState<ISandbox>(
        sandbox,
    );
    const [sshConfig, setSshConfig] = useState(null);
    const [loading, setLoading] = useState<string | undefined>(undefined);
    const [showNetworkInfo, setShowNetworkInfo] = useState<boolean>(false);
    const sandboxHost = getSandboxHost(sandboxLocalState.web);
    const httpPort80Url = sandboxLocalState.web.replace('PORT', '80');

    const getStatusLabel = (status: string): string => {
        const statusMap = {
            'RUNNING': window.I18n.t('sandboxes.status_running', 'RUNNING'),
            'PENDING': window.I18n.t('sandboxes.status_pending', 'PENDING'),
            'CREATING': window.I18n.t('sandboxes.status_creating', 'CREATING'),
            'DESTROYING': window.I18n.t('sandboxes.status_destroying', 'DESTROYING'),
        };
        return statusMap[status] || status;
    };

    useEffect(() => {
        const monitorSandboxSpawning = async (): Promise<void> => {
            if (sandboxLocalState.arn === '') {
                return;
            }

            try {
                const {
                    data: monitoredSandbox,
                    success,
                }: { data: ISandbox; success: boolean } = await get(
                    `${sandboxesUri}/${encodeURIComponent(
                        sandboxLocalState.arn,
                    )}.json`,
                    csrfToken,
                );

                if (success === false) {
                    alert(
                        'Error while monitoring the sandbox. Please refresh this page.',
                    );
                    setNeedRefresh(false);
                    return;
                }

                setSandboxLocalState(monitoredSandbox);
            } catch (err) {
                console.log(err);
                setNeedRefresh(false);
            }
        };

        if (sandboxLocalState.status === 'RUNNING') {
            setLoading(undefined);

            return;
        }

        if (sandboxLocalState.status === 'DESTROYING') {
            setLoading('all');

            return;
        }

        setLoading('all');
        monitorSandboxSpawning();
    }, [sandboxLocalState]);

    const wakeSandbox = async (): Promise<void> => {
        setLoading('wake');
        try {
            const {data, status}: {data: number, status: boolean} = await post(
                sandboxLocalState.renew_autostop_uri,
                csrfToken,
                {},
            );
            
            if (status === false) {
                alert('Error while waking up the sandbox. Try again later.');
                return;
            }

            setSandboxLocalState({ ...sandboxLocalState, auto_stop_in_mins: data });
        } catch (err) {
            console.error(err);
            alert('Error while waking the sandbox. Try again later.');
        } finally {
            setLoading(undefined);
        }
    };

    const onClickDeleteSandbox = async (): Promise<void> => {
        setLoading('delete');
        setSandboxLocalState({ ...sandboxLocalState, status: 'DESTROYING' });

        try {
            await deleteSandbox(sandboxLocalState);
        } catch (error) {
            alert(error);
            setLoading(undefined);
        }
    };

    const getSshConfig = async (): Promise<void> => {
        setLoading('ssh');
        try {
            const resp = await get(sandbox.ssh_uri, csrfToken);
            setSshConfig(resp.ssh_config);
        } catch (err) {
            throw err;
        } finally {
            setLoading(undefined);
        }
    };

    if (sandboxLocalState.status === 'CREATING') {
        return (
            <div className="panel panel-default sandbox-card">
                <div className="panel-body" style={{ padding: '32px 24px', textAlign: 'center' }}>
                    <div className="sandbox-card-header" style={{ border: 'none', paddingBottom: 0, marginBottom: 24 }}>
                        <div className="sandbox-card-title" style={{ flex: 'none', margin: '0 auto' }}>
                            <i className="fa fa-terminal sandbox-card-icon" />
                            <span className="sandbox-card-name">
                                {window.I18n.t('sandboxes.creating_sandbox', 'Creating sandbox')} {sandboxLocalState.image?.name || ''}...
                            </span>
                        </div>
                    </div>
                    <i className="fa fa-solid fa-spinner fa-spin" style={{ fontSize: 36, color: '#6c757d' }} />
                </div>
            </div>
        );
    }

    const titleText = sandboxLocalState.task
        ? `${sandboxLocalState.aws_region} – ${sandboxLocalState.task}`
        : sandboxLocalState.image?.name
            ? `${sandboxLocalState.aws_region} – ${sandboxLocalState.image.name}`
            : sandboxLocalState.aws_region;

    return (
        <div className="panel panel-default sandbox-card">
            <div className="panel-body">
                <div className="sandbox-card-header">
                    <div className="sandbox-card-title">
                        <i className="fa fa-terminal sandbox-card-icon" />
                        <span className="sandbox-card-name">{titleText}</span>
                    </div>
                    <div className="sandbox-card-badges">
                        <span
                            className={`label label-${
                                sandboxLocalState.status === 'RUNNING'
                                    ? 'success'
                                    : sandboxLocalState.status === 'DESTROYING'
                                    ? 'danger'
                                    : 'warning'
                            }`}
                            style={{ padding: '6px 12px', fontSize: 12 }}
                        >
                            {getStatusLabel(sandboxLocalState.status)}
                        </span>
                        <span
                            className={`label label-${
                                sandboxLocalState.auto_stop_in_mins > 30
                                    ? 'info'
                                    : 'warning'
                            }`}
                            style={{ padding: '6px 12px', fontSize: 12 }}
                        >
                            {sandboxLocalState.auto_stop_in_mins} {window.I18n.t('sandboxes.minutes_remaining', 'minutes remaining')}
                        </span>
                    </div>
                </div>
                <div className="sandbox-card-connections">
                    {sandboxLocalState.url_vscode && (
                        <a
                            className={`sandbox-connection-btn ${loading !== undefined ? 'disabled' : ''}`}
                            href={sandboxLocalState.url_vscode}
                            rel="noreferrer"
                            tabIndex={loading !== undefined ? 0 : -1}
                            target="_blank"
                        >
                            <i className="fa fa-code" /> VSCode
                        </a>
                    )}
                    <a
                        className={`sandbox-connection-btn ${loading !== undefined ? 'disabled' : ''}`}
                        href="#"
                        onClick={getSshConfig}
                        tabIndex={loading !== undefined ? 0 : -1}
                    >
                        <i className="fa fa-terminal" /> SSH
                    </a>
                    <a
                        className={`sandbox-connection-btn ${loading !== undefined ? 'disabled' : ''}`}
                        href={sandboxLocalState.url_ttyd}
                        rel="noreferrer"
                        tabIndex={loading !== undefined ? 0 : -1}
                        target="_blank"
                        title={window.I18n.t(
                            isSafari ? 'sandboxes.ttyd_tooltip_safari' : 'sandboxes.ttyd_tooltip',
                            isSafari
                                ? 'This web terminal does not work in Safari. Use Chrome, Firefox, or Edge instead.'
                                : 'Open web terminal in a new tab',
                        )}
                    >
                        <i className="fa fa-desktop" />{' '}
                        {window.I18n.t('sandboxes.web_terminal', 'Web terminal')}
                    </a>
                    <button
                        className={`sandbox-connection-btn ${loading !== undefined ? 'disabled' : ''}`}
                        disabled={loading !== undefined}
                        onClick={(): void => setShowNetworkInfo(!showNetworkInfo)}
                        type="button"
                    >
                        <i className="fa fa-sitemap" />{' '}
                        {window.I18n.t(
                            'sandboxes.network_informations',
                            'Network information',
                        )}
                    </button>
                </div>
                {isSafari && (
                    <div className="alert alert-warning" style={{ marginBottom: 20 }}>
                        {window.I18n.t(
                            'sandboxes.ttyd_safari_warning',
                            '⚠️ Warning: This web terminal does not work in Safari. Please use Chrome, Firefox, or Edge instead.',
                        )}
                    </div>
                )}
                <div className="sandbox-card-actions">
                    <button
                        className="btn btn-success"
                        disabled={loading !== undefined}
                        onClick={(): void => void wakeSandbox()}
                        style={{ minWidth: 140 }}
                    >
                        {['wake', 'all'].includes(loading) ? (
                            <i className="fa fa-solid fa-spinner fa-spin" />
                        ) : (
                            <><i className="fa fa-clock-o" /> {window.I18n.t('sandboxes.add_more_time', 'Add more time')}</>
                        )}
                    </button>
                    <button
                        className="btn btn-danger"
                        disabled={loading !== undefined}
                        onClick={onClickDeleteSandbox}
                        style={{ minWidth: 140 }}
                    >
                        {['delete', 'all'].includes(loading) ? (
                            <i className="fa fa-solid fa-spinner fa-spin" />
                        ) : (
                            <><i className="fa fa-trash" /> {window.I18n.t('sandboxes.delete_sandbox', 'Delete sandbox')}</>
                        )}
                    </button>
                </div>
                <div className="mt-3">
                    {showNetworkInfo && (
                        <div className="sandbox-help-panel">
                            <div style={{ display: 'flex', alignItems: 'start', gap: '10px' }}>
                                <i className="fa fa-info-circle" style={{ color: '#17a2b8', fontSize: '18px', marginTop: '2px' }}></i>
                                <div style={{ flex: 1 }}>
                                    <strong style={{ display: 'block', marginBottom: '12px' }}>
                                        {window.I18n.t(
                                            'sandboxes.network_informations',
                                            'Network information',
                                        )}
                                    </strong>
                                    {sandboxHost && (
                                        <div style={{ marginBottom: '16px' }}>
                                            <strong style={{ display: 'block', marginBottom: '4px' }}>
                                                {window.I18n.t(
                                                    'sandboxes.network_hostname',
                                                    'Hostname',
                                                )}
                                            </strong>
                                            <CopyOnClick copyText={sandboxHost}>
                                                <code>{sandboxHost}</code>
                                            </CopyOnClick>
                                        </div>
                                    )}
                                    {sandboxLocalState.private_ip && (
                                        <div style={{ marginBottom: '16px' }}>
                                            <strong style={{ display: 'block', marginBottom: '4px' }}>
                                                {window.I18n.t(
                                                    'sandboxes.network_local_ip',
                                                    'Local IP',
                                                )}
                                            </strong>
                                            <CopyOnClick copyText={sandboxLocalState.private_ip}>
                                                <code>{sandboxLocalState.private_ip}</code>
                                            </CopyOnClick>
                                        </div>
                                    )}
                                    <div style={{ marginBottom: '12px' }}>
                                        <a
                                            className={`sandbox-connection-btn ${loading !== undefined ? 'disabled' : ''}`}
                                            href={httpPort80Url}
                                            rel="noreferrer"
                                            tabIndex={loading !== undefined ? 0 : -1}
                                            target="_blank"
                                        >
                                            <i className="fa fa-globe" /> HTTP - Port 80
                                        </a>
                                    </div>
                                    <p style={{ margin: '0 0 8px 0', lineHeight: '1.5' }}>
                                        {window.I18n.t('sandboxes.http_port_80_explanation', 'This button opens a new tab in your browser to port 80 of the sandbox.')}
                                    </p>
                                    <p style={{ margin: '0 0 8px 0', lineHeight: '1.5' }}>
                                        {window.I18n.t('sandboxes.http_port_80_no_service', 'By default, no service is listening on this port, so if you want to test your website for example, you need to start a web server first.')}
                                    </p>
                                    <p style={{ margin: '0', lineHeight: '1.5' }}>
                                        <strong>{window.I18n.t('sandboxes.http_port_80_examples', 'Examples:')}</strong>
                                    </p>
                                    <pre style={{ backgroundColor: '#2d2d2d', color: '#f8f8f2', padding: '10px', borderRadius: '4px', marginTop: '8px', marginBottom: '0' }}>
                                        <code>
                                            # {window.I18n.t('sandboxes.http_port_80_python', 'Using Python')}<br />
                                            python3 -m http.server 80<br />
                                            <br />
                                            # {window.I18n.t('sandboxes.http_port_80_nginx', 'Or install and start nginx')}<br />
                                            apt-get update && apt-get install -y nginx<br />
                                            service nginx start
                                        </code>
                                    </pre>
                                </div>
                            </div>
                        </div>
                    )}
                    {loading === 'ssh' ? (
                        <div className="sandbox-ssh-config">{window.I18n.t('sandboxes.generating_ssh_config', 'Generating ssh config...')}</div>
                    ) : (
                        sshConfig !== null && (
                            <div className="sandbox-ssh-config">
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>SSH configuration</div>
                                <pre>
                                    $> ssh -p {sshConfig.bind_port} root@
                                    {sshConfig.ssh}
                                    <br />
                                    Password: {sandboxLocalState.tkn}
                                </pre>
                            </div>
                        )
                    )}
                    {isSafari && (
                        <div className="sandbox-ssh-config">
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>TTY credentials</div>
                            <pre>
                                Login:{' '}
                                {
                                    sandboxLocalState.url_ttyd.match(
                                        /.*\/([^\/:]*):/,
                                    )[1]
                                }
                                <br />
                                Password:{' '}
                                {
                                    sandboxLocalState.url_ttyd.match(
                                        /.*:(.*)@.*/,
                                    )[1]
                                }
                            </pre>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
